import { useState, useEffect } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, Settings, Link, Phone, Mail, MapPin, Image, Linkedin, Facebook } from 'lucide-react';

const AdminSettings = () => {
  const { API, setSettings } = useGlobal();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    teams_scheduling_url: '',
    motorcycle_ecommerce_url: '',
    company_name: '',
    company_phone: '',
    company_email: '',
    company_address: '',
    logo_url: '',
    linkedin_url: '',
    facebook_url: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/global-settings`);
        setFormData({
          teams_scheduling_url: response.data.teams_scheduling_url || '',
          motorcycle_ecommerce_url: response.data.motorcycle_ecommerce_url || '',
          company_name: response.data.company_name || '',
          company_phone: response.data.company_phone || '',
          company_email: response.data.company_email || '',
          company_address: response.data.company_address || '',
          logo_url: response.data.logo_url || '',
          linkedin_url: response.data.linkedin_url || '',
          facebook_url: response.data.facebook_url || ''
        });
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/global-settings`, formData, { headers });
      setSettings(prev => ({ ...prev, ...response.data }));
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-slate-100 animate-pulse h-20 rounded-sm" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 font-['Montserrat']">Global Settings</h1>
        <p className="text-slate-600">Configure site-wide settings and links</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Links Section */}
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 font-['Montserrat'] flex items-center gap-2">
            <Link className="w-5 h-5 text-[#5b3589]" />
            External Links
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="teams_url">Microsoft Teams Scheduling URL</Label>
              <Input
                id="teams_url"
                value={formData.teams_scheduling_url}
                onChange={(e) => setFormData(prev => ({ ...prev, teams_scheduling_url: e.target.value }))}
                placeholder="https://outlook.office.com/bookwithme/..."
              />
              <p className="text-xs text-slate-500">This URL will be used for all "Schedule a Teams Call" buttons</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ecommerce_url">Motorcycle E-commerce Store URL</Label>
              <Input
                id="ecommerce_url"
                value={formData.motorcycle_ecommerce_url}
                onChange={(e) => setFormData(prev => ({ ...prev, motorcycle_ecommerce_url: e.target.value }))}
                placeholder="https://www.anchilex.com"
              />
            </div>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 font-['Montserrat'] flex items-center gap-2">
            <Linkedin className="w-5 h-5 text-[#5b3589]" />
            Social Media
          </h2>
          <p className="text-sm text-slate-500 mb-4">Leave blank to hide the icon from the footer</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="linkedin" className="flex items-center gap-2">
                <Linkedin className="w-4 h-4" />
                LinkedIn URL
              </Label>
              <Input
                id="linkedin"
                value={formData.linkedin_url}
                onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                placeholder="https://www.linkedin.com/company/..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="facebook" className="flex items-center gap-2">
                <Facebook className="w-4 h-4" />
                Facebook URL
              </Label>
              <Input
                id="facebook"
                value={formData.facebook_url}
                onChange={(e) => setFormData(prev => ({ ...prev, facebook_url: e.target.value }))}
                placeholder="https://www.facebook.com/..."
              />
            </div>
          </div>
        </div>

        {/* Contact Info Section */}
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 font-['Montserrat'] flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#5b3589]" />
            Contact Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="company_name" className="flex items-center gap-2">
                Company Name (shown in footer)
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Trinity Electronics Co., LTD 美雅電子股份有限公司"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number
              </Label>
              <Input
                id="phone"
                value={formData.company_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, company_phone: e.target.value }))}
                placeholder="+886-2-XXXX-XXXX"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.company_email}
                onChange={(e) => setFormData(prev => ({ ...prev, company_email: e.target.value }))}
                placeholder="info@trinity-tw.com"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Company Address
              </Label>
              <Input
                id="address"
                value={formData.company_address}
                onChange={(e) => setFormData(prev => ({ ...prev, company_address: e.target.value }))}
                placeholder="Taipei, Taiwan"
              />
            </div>
          </div>
        </div>

        {/* Branding Section */}
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 font-['Montserrat'] flex items-center gap-2">
            <Image className="w-5 h-5 text-[#5b3589]" />
            Branding
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="logo">Logo URL</Label>
              <Input
                id="logo"
                value={formData.logo_url}
                onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            {formData.logo_url && (
              <div className="p-4 bg-slate-900 rounded-sm inline-block">
                <img 
                  src={formData.logo_url} 
                  alt="Logo preview" 
                  className="h-12 w-auto"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={saving}
            className="bg-[#5b3589] hover:bg-[#4c1d95] text-white rounded-sm"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
